import slaydApi from './axiosInstance';

export interface VideoPostKeyframes {
  childImages: string[];
}

export type FetchFramesByVideoIdResponse = VideoPostKeyframes[];

export const fetchFramesByVideoId = async (
  videoId: string
): Promise<FetchFramesByVideoIdResponse> => {
  try {
    const response = await slaydApi.get<FetchFramesByVideoIdResponse>(
      '/instagram/video-posts/',
      {
        params: { video_id: videoId },
      }
    );

    if (
      !response.data ||
      !Array.isArray(response.data) ||
      !response.data.length ||
      !response.data[0].childImages
    )
      throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(`Error fetching keyframes`);
  }
};

export const fetchKeyframesForVideoId = async (
  videoId: string
): Promise<string[]> => {
  const response = await fetchFramesByVideoId(videoId);

  return response[0].childImages;
};

export const sendFrameSelectionBeacon = (
  videoId: string,
  frameUrl: string,
  senderId: string
): void => {
  if (!senderId || senderId === '') return;

  const baseURL = import.meta.env.VITE_API_BASE_URL;
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Token ${token}`;
  }

  fetch(`${baseURL}/instagram/frame-selection/`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      video_id: videoId,
      frame_image_url: frameUrl,
      sender_id: senderId,
    }),
    keepalive: true, // Ensures request completes even if page unloads
  }).catch((err) => {
    console.error('Failed to send frame selection beacon:', err);
  });
};

export interface UploadBatchResponse {
  batchId: string;
  firestoreCollection: string;
  documentId: string;
  status: 'processing';
  totalCount: number;
  message: string;
}

// For unauthenticated + multiple images
export interface UploadUrlsResponse {
  urls: string[];
}

// For authenticated + single image
export interface UploadSingleResponse {
  url: string;
  fileName: string;
}

// Legacy response types (for backward compatibility)
export interface UploadImageResponse {
  url: string;
}

export interface UploadResult {
  url: string;
  fileName: string;
}

export interface UploadMultipleImagesResponse {
  uploadedCount: number;
  results: UploadResult[];
}

// Union type for all possible responses
export type UploadResponse =
  | UploadBatchResponse
  | UploadUrlsResponse
  | UploadSingleResponse
  | UploadImageResponse
  | UploadMultipleImagesResponse;

export const uploadImages = async (
  images: File | File[]
): Promise<UploadResponse> => {
  const imageArray = Array.isArray(images) ? images : [images];
  const isSingle = !Array.isArray(images);

  const formData = new FormData();

  imageArray.forEach((image) => {
    formData.append('image', image, image.name);
  });

  try {
    const response = await slaydApi.post<UploadResponse>(
      '/activity/upload/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const data = response.data;

    // Check for batch response (authenticated + multiple images)
    if (
      'batchId' in data &&
      'documentId' in data &&
      'status' in data &&
      'totalCount' in data
    ) {
      return data as UploadBatchResponse;
    }

    // Check for URLs array response (unauthenticated + multiple images)
    if ('urls' in data && Array.isArray(data.urls)) {
      return data as UploadUrlsResponse;
    }

    // Check for single image response with fileName (authenticated + single)
    if ('url' in data && 'fileName' in data) {
      return data as UploadSingleResponse;
    }

    // Check for single image response without fileName (unauthenticated + single or legacy)
    if ('url' in data && data.url) {
      return data as UploadImageResponse;
    }

    // Legacy multiple images format (for backward compatibility)
    if ('results' in data && Array.isArray(data.results)) {
      return data as UploadMultipleImagesResponse;
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error(error);
    throw new Error(`Error uploading ${isSingle ? 'image' : 'images'}`);
  }
};

export const uploadImage = async (
  image: File
): Promise<UploadImageResponse | UploadSingleResponse> => {
  const response = await uploadImages(image);
  if ('url' in response && !('urls' in response) && !('results' in response)) {
    return response;
  }
  throw new Error(
    'Expected single image response but received batch or multiple images response'
  );
};

export const uploadMultipleImages = async (
  images: File[]
): Promise<UploadMultipleImagesResponse> => {
  const response = await uploadImages(images);
  if ('results' in response && Array.isArray(response.results)) {
    return response;
  }
  throw new Error(
    'Expected multiple images response (with results array) but received batch, single, or other response format'
  );
};
